import { useQuery } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import HourglassTop from "@mui/icons-material/HourglassTop";
import PersonAdd from "@mui/icons-material/PersonAdd";
import Save from "@mui/icons-material/Save";
import Send from "@mui/icons-material/Send";
import Visibility from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { shouldAdvanceWithoutReview } from "components/Submission/advanceWithoutReview";
import { DocumentUploadSection } from "components/Submission/DocumentUploadSection";
import { EmbeddedDocumentPreparationUrlDialog } from "components/Submission/EmbeddedSignature/EmbeddedDocumentPreparationUrlDialog";
import { EmbeddedDocumentSigning } from "components/Submission/EmbeddedSignature/EmbeddedDocumentSigning";
import {
  partitionReviewersAndSigners,
  sortSubmissionDocumentsByType,
  toSubmissionDocumentDisplay as toDisplayDocument,
} from "components/Submission/helpers/documentHelpers";
import {
  dueDateWarning,
  signatureRequestExpiry,
} from "components/Submission/helpers/dueDateWarnings";
import {
  buildOrderedConfig,
  serializeConfig,
  toReviewerInputs,
  toUiReviewer,
  toUiSigner,
} from "components/Submission/helpers/reviewerConfiguration";
import { RejectionRoutingDialog } from "components/Submission/RejectionRoutingDialog";
import { ReviewerManagementDialog } from "components/Submission/ReviewerManagementDialog";
import { Reviewer } from "components/Submission/ReviewersEditList";
import { ReviewersDatagrid } from "components/Submission/ReviewersList";
import { SaveAndPrepareForSigningButton } from "components/Submission/SaveAndPrepareForSigningButton";
import { SignatureConfirmationAlert } from "components/Submission/SignatureConfirmationAlert";
import { SignaturePreparationBanner } from "components/Submission/SignaturePreparationBanner";
import { SignerDialog } from "components/Submission/SignerDialog";
import { SignersDatagrid } from "components/Submission/SignersDatagrid";
import { SubmissionDisplayReviewOrSignDocument } from "components/Submission/SubmissionDisplayReviewOrSignDocument";
import { SubmissionDocumentPreview } from "components/Submission/SubmissionDocumentPreview";
import { SubmissionReadOnlySummary } from "components/Submission/SubmissionReadOnlySummary";
import { SubmissionRevisionBanner } from "components/Submission/SubmissionRevisionBanner";
import { SubmissionStatusChipWithPopover } from "components/Submission/SubmissionStatusChipWithPopover";
import { getMoveButtonLabel } from "components/Submission/UpdateSubmissionStatusButton";
import { UnfinishedChangesPrompt } from "components/UnfinishedChangesPrompt";
import { SubmissionFormProvider } from "contexts/SubmissionFormContext";
import {
  Issue,
  ReviewerStatus,
  ReviewMode,
  SignatureStatus,
  SignatureType,
  SubmissionDocumentType,
  SubmissionEncampStatus,
  SubmissionStatus,
  SubmissionStepStatus,
  SubmissionStepType,
} from "generated-graphql/graphql";
import { computeNeedsDocumentPreparation } from "hooks/submission/needsDocumentPreparation";
import {
  computeSubmissionReviewStates,
  SubmissionReviewState,
} from "hooks/submission/reviewStates";
import { useSignatureConfirmation } from "hooks/submission/useSignatureConfirmation";
import { useSubmissionDocumentPreview } from "hooks/submission/useSubmissionDocumentPreview";
import { useCurrentUser } from "hooks/useCurrentUser";
import { SubmissionFormData, SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import {
  useApproveSubmissionDocumentReview,
  useAssignPeerReviewToMe,
  useMoveToReview,
  useRejectSubmissionDocumentReview,
  useUpdateSubmission,
} from "hooks/useSubmissionMutations";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useWasteStepper } from "hooks/useWasteStepper";
import { DateTime } from "luxon";
import { GET_EMBEDDED_DOCUMENT_SIGNING_URL } from "queries/submissions";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useSubmissionInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useSubmissionInputValidation";
import { pickTransitionErrorMessage } from "routes/Customer/Waste/Submissions/ReadOnly/pickTransitionErrorMessage";
import { useDebouncedCallback } from "use-debounce";
import { parseDateLikeToLocalDateTime } from "util/dates";
import { buildWasteFacilityUrl } from "util/wasteFacilityUrl";

import { DetailsCard } from "../DetailsCard";
import { StepHeader } from "../StepHeader";

type ReviewFormValues = { reviewFinished: boolean; rejectionComment: string };

/**
 * Review & sign step: staff configure reviewers/signature and send for review;
 * reviewers and signers act on their part here, gated by the review state.
 */
export function ReviewAndSignStep({
  submissionId,
  facilityId,
}: {
  submissionId: string;
  facilityId: string;
}) {
  const alerts = useAlerts();
  const { tenantId = "" } = useParams();
  const { isStaff, user } = useCurrentUser();
  const { submission, canEdit, isFrozen, refetchSubmission } = useWasteStepper();

  const [moveToReview, { loading: sending }] = useMoveToReview();
  const [updateSubmission, { loading: saving }] = useUpdateSubmission();
  const [approveDocumentReview, { loading: approving }] = useApproveSubmissionDocumentReview();
  const [rejectDocumentReview, { loading: rejecting }] = useRejectSubmissionDocumentReview();
  const [assignPeerReviewToMe, { loading: claiming }] = useAssignPeerReviewToMe();
  const { touchStep } = useTouchSubmissionMutation(submissionId);

  const status = submission?.status ?? SubmissionStatus.InProgress;
  const persistedReviewers = useMemo(() => submission?.reviewers ?? [], [submission]);
  const documents = useMemo(() => submission?.documents ?? [], [submission]);

  // Staff can re-send from their own initial review; it's the only way to
  // re-route after a config change at that status.
  const canSendForReview =
    status === SubmissionStatus.NotStarted ||
    status === SubmissionStatus.InProgress ||
    (isStaff && status === SubmissionStatus.EncampInitialReview);
  const isRejected = submission?.encampStatus === SubmissionEncampStatus.Rejected;
  // Rejections are resolved through rejection routing, not config edits.
  const disabled = !canEdit || isFrozen || isRejected;

  // --- Local, editable config state ---------------------------------------
  const [signatureType, setSignatureType] = useState<SignatureType>(
    submission?.signatureType ?? SignatureType.None
  );
  const [reviewers, setReviewers] = useState<Reviewer[]>(() =>
    persistedReviewers.filter((r) => !r.isSigner).map(toUiReviewer)
  );
  const [signer, setSigner] = useState<SubmissionReviewerSigner | null>(() => {
    const persistedSigner = persistedReviewers.find((r) => r.isSigner);
    return persistedSigner ? toUiSigner(persistedSigner) : null;
  });
  const [reviewRequired, setReviewRequired] = useState<boolean>(submission?.reviewRequired ?? true);
  const [peerReviewRequired, setPeerReviewRequired] = useState<boolean>(
    submission?.peerReviewRequired ?? false
  );
  // The reviewers datagrid drives review mode through a react-hook-form
  // setValue; this local form exists only to satisfy that contract.
  const reviewModeForm = useForm<SubmissionFormData>({
    defaultValues: { reviewMode: submission?.reviewMode ?? ReviewMode.Parallel },
  });
  const reviewMode = reviewModeForm.watch("reviewMode") ?? ReviewMode.Parallel;

  // "No review required" has to persist as no review. The backend routes the
  // review phase off the saved reviewer rows and the peer-review flag, so
  // sending reviewers the UI has hidden puts the submission into review anyway.
  // These stay out of every payload while the list itself survives in local
  // state, so toggling back before a save restores it.
  // Memoized: the empty case would otherwise be a new array every render and
  // retrigger the validation effect in a loop.
  const reviewersToPersist = useMemo(
    () => (reviewRequired ? reviewers : []),
    [reviewRequired, reviewers]
  );
  const peerReviewToPersist = reviewRequired && peerReviewRequired;

  // Mutations await their stepper refetch, so a save reads clean on resolve.
  const persistedConfig = useMemo(() => {
    const persistedSigner = persistedReviewers.find((r) => r.isSigner);
    return serializeConfig({
      reviewers: persistedReviewers.filter((r) => !r.isSigner).map(toUiReviewer),
      signer: persistedSigner ? toUiSigner(persistedSigner) : null,
      signatureType: submission?.signatureType ?? SignatureType.None,
      reviewRequired: submission?.reviewRequired ?? true,
      peerReviewRequired: submission?.peerReviewRequired ?? false,
      reviewMode: submission?.reviewMode ?? ReviewMode.Parallel,
    });
  }, [persistedReviewers, submission]);
  // Compare what a save would actually write, so dropping hidden reviewers
  // doesn't read as a pending change.
  const hasUnsavedChanges =
    serializeConfig({
      reviewers: reviewersToPersist,
      signer,
      signatureType,
      reviewRequired,
      peerReviewRequired: peerReviewToPersist,
      reviewMode,
    }) !== persistedConfig;

  // Re-sync local config only when the persisted config actually changes, so an
  // unrelated refetch doesn't clobber unsaved reviewer/signer edits.
  useEffect(() => {
    setReviewers(persistedReviewers.filter((r) => !r.isSigner).map(toUiReviewer));
    const persistedSigner = persistedReviewers.find((r) => r.isSigner);
    setSigner(persistedSigner ? toUiSigner(persistedSigner) : null);
    setSignatureType(submission?.signatureType ?? SignatureType.None);
    setReviewRequired(submission?.reviewRequired ?? true);
    setPeerReviewRequired(submission?.peerReviewRequired ?? false);
    reviewModeForm.setValue("reviewMode", submission?.reviewMode ?? ReviewMode.Parallel);
    // All values read here are encoded in persistedConfig; setValue is stable.
    // oxlint-disable-next-line exhaustive-deps
  }, [persistedConfig]);

  // --- Dialog + review-action state ---------------------------------------
  const [reviewerDialogOpen, setReviewerDialogOpen] = useState(false);
  const [signerDialogOpen, setSignerDialogOpen] = useState(false);
  const [preparationDialogOpen, setPreparationDialogOpen] = useState(false);
  const [rejectionRoutingOpen, setRejectionRoutingOpen] = useState(false);
  const [signingDialogOpen, setSigningDialogOpen] = useState(false);
  const [advanceConfirmOpen, setAdvanceConfirmOpen] = useState(false);
  const [finalApprovalReviewers, setFinalApprovalReviewers] = useState<Reviewer[] | null>(null);
  // Signer/reviewer mutual-exclusion confirmations (one person can't be both).
  // The pending save carries its review mode so confirming applies exactly what
  // the dialog chose, not just the reviewer list.
  const [reviewersToConfirm, setReviewersToConfirm] = useState<{
    reviewers: Reviewer[];
    reviewMode: ReviewMode;
  } | null>(null);
  const [signerToConfirm, setSignerToConfirm] = useState<SubmissionReviewerSigner | null>(null);

  const {
    signingCompleted,
    signingFlowConfirmed,
    signingConfirmationPending,
    signatureConfirmationState,
    isAwaitingPostSignatureTransition,
    markSignatureSubmitted,
    retrySignatureConfirmation,
  } = useSignatureConfirmation({
    currentUserId: user?.id,
    reviewers: persistedReviewers,
    submissionStatus: status,
    refetchSubmission,
  });

  const { control, handleSubmit, watch, formState } = useForm<ReviewFormValues>({
    defaultValues: { reviewFinished: true, rejectionComment: "" },
  });
  const reviewFinished = watch("reviewFinished");
  const rejectionComment = watch("rejectionComment");

  // --- Review-state + preparation gating ----------------------------------
  const { reviewState, isReviewerASigner, currentUsersSubmissionDocumentReviewId } = useMemo(
    () => computeSubmissionReviewStates({ currentUser: user, submissionData: { submission } }),
    [user, submission]
  );

  const isDigitalSignature = signatureType === SignatureType.Digital;
  const needsDocumentPreparation = useMemo(
    () =>
      computeNeedsDocumentPreparation({
        isDigitalSignature,
        isEditMode: true,
        defaultValues: {
          reviewers: persistedReviewers.map((r) => ({ userId: r.user.id, isSigner: r.isSigner })),
        },
        signer,
        submissionData: { submission },
        submissionDocuments: documents
          .filter((d) => d.type === SubmissionDocumentType.Submission)
          .map((d) => ({ documentId: d.document.id, type: d.type })),
      }),
    [isDigitalSignature, persistedReviewers, signer, submission, documents]
  );
  // Match the legacy form's recovery path: if an in-review signing request is
  // missing or was reset, staff must be able to prepare it again without
  // exposing the status-transition action a second time.
  const canPrepareForSigning =
    canSendForReview || (isStaff && status === SubmissionStatus.InReview);

  // --- Validation + due-date warnings for the reviewer/signer UI ----------
  const validationDocuments = useMemo(
    () => documents.map((d) => ({ documentId: d.document.id, type: d.type })),
    [documents]
  );
  const validateSubmissionInput = useSubmissionInputValidation(validationDocuments);
  const [issues, setIssues] = useState<Issue[]>([]);

  const revalidate = useCallback(async (): Promise<Issue[]> => {
    // Validate the config a save would write, not the one on screen.
    const ordered = buildOrderedConfig(reviewersToPersist, signer);
    const result = await validateSubmissionInput({
      id: submissionId,
      // tenantId is overwritten by the validation hook (from the route params);
      // it's supplied here only to satisfy the required input field.
      tenantId,
      title: submission?.title ?? "",
      recipient: submission?.recipient ?? "",
      // The submission-level fields are not editable on this step but the
      // validator requires them, so pass the persisted values through.
      dueDate: submission?.dueDate ?? undefined,
      submissionType: submission?.submissionType,
      submissionMethod: submission?.submissionMethod ?? undefined,
      facilityIds: (submission?.facilitySubmissions ?? []).map((fs) => fs.facility.id),
      reviewRequired,
      peerReviewRequired: peerReviewToPersist,
      reviewMode,
      signatureType,
      reviewers: ordered.map((c) => ({
        userId: c.id,
        reviewOrder: c.reviewOrder,
        isStaff: c.isStaff,
        isRequired: true,
        isSigner: c.isSigner,
        dueDate: c.dueDate,
      })),
    });
    setIssues(result);
    return result;
  }, [
    reviewersToPersist,
    signer,
    validateSubmissionInput,
    submissionId,
    tenantId,
    submission?.title,
    submission?.recipient,
    submission?.dueDate,
    submission?.submissionType,
    submission?.submissionMethod,
    submission?.facilitySubmissions,
    reviewRequired,
    peerReviewToPersist,
    reviewMode,
    signatureType,
  ]);

  // Edits land a field at a time; 500ms matches useValidatingForm in the legacy form.
  const debouncedRevalidate = useDebouncedCallback(revalidate, 500);

  useEffect(() => {
    void debouncedRevalidate();
    return () => debouncedRevalidate.cancel();
  }, [debouncedRevalidate]);

  // Nobody can act on issues once a submission is completed.
  const displayIssues = useMemo(
    () =>
      status === SubmissionStatus.FilingComplete || status === SubmissionStatus.NotReporting
        ? []
        : issues,
    [status, issues]
  );

  const reviewRequiredIssues = useMemo(
    () => displayIssues.filter((i) => i.key === "reviewRequired"),
    [displayIssues]
  );
  const signatureTypeIssues = useMemo(
    () => displayIssues.filter((i) => i.key === "signatureType"),
    [displayIssues]
  );

  // What's left has no control on this step to attach to — mostly submission-level
  // fields from the Details card. Without this it only ever shows up as a disabled
  // Send for review with no stated reason.
  const unattributedIssues = useMemo(
    () =>
      displayIssues.filter(
        (i) =>
          i.modelName !== "SubmissionReviewer" &&
          !(i.modelName === "Submission" && i.key === "reviewers") &&
          i.key !== "documents" &&
          i.key !== "reviewRequired" &&
          i.key !== "signatureType"
      ),
    [displayIssues]
  );

  const signatureRequestExpiresAt = useMemo(
    () => signatureRequestExpiry(documents, DateTime.utc().startOf("day"), signatureType),
    [documents, signatureType]
  );

  const dueDateWarnings = useMemo(() => {
    const warnings: Record<string, string> = {};
    const today = DateTime.utc().startOf("day");
    for (const config of buildOrderedConfig(reviewers, signer)) {
      if (!config.dueDate?.isValid) continue;
      const due = DateTime.utc(config.dueDate.year, config.dueDate.month, config.dueDate.day);
      const warning = dueDateWarning({
        dueDate: due,
        isSigner: config.isSigner === true,
        signatureRequestExpiresAt,
        today,
      });
      if (warning) {
        warnings[config.id] = warning;
      }
    }
    return warnings;
  }, [reviewers, signer, signatureRequestExpiresAt]);

  // --- Document open link --------------------------------------------------
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const selectedDocument = useMemo(() => {
    const explicit = selectedDocumentId
      ? documents.find((d) => d.document.id === selectedDocumentId)
      : undefined;
    return (
      explicit ??
      documents.find((d) => d.type === SubmissionDocumentType.Submission) ??
      documents.find((d) => d.type === SubmissionDocumentType.Attachment) ??
      null
    );
  }, [documents, selectedDocumentId]);
  const persistedSignerForSigning = persistedReviewers.find((reviewer) => reviewer.isSigner);
  const isSelectedDocumentPreparedForSigning =
    selectedDocument?.type === SubmissionDocumentType.Submission &&
    selectedDocument.externalDocumentId != null &&
    (persistedSignerForSigning?.signatureStatus === SignatureStatus.Sent ||
      persistedSignerForSigning?.signatureStatus === SignatureStatus.Completed);
  const selectedDocumentPreviewId = selectedDocument?.document.id ?? null;
  const handleDocumentPreviewError = useCallback(
    (error: unknown) => {
      alerts.error("Unable to preview this document.", error);
    },
    [alerts]
  );
  const { documentUrl, hasError: documentPreviewFailed } = useSubmissionDocumentPreview({
    documentId: selectedDocumentPreviewId,
    inline: true,
    onError: handleDocumentPreviewError,
  });

  const { data: signingUrlData } = useQuery(GET_EMBEDDED_DOCUMENT_SIGNING_URL, {
    variables: { submissionId },
    fetchPolicy: "network-only",
    skip:
      status !== SubmissionStatus.InReview ||
      reviewState !== SubmissionReviewState.SIGNER_CAN_SIGN ||
      !isSelectedDocumentPreparedForSigning,
  });

  // --- Change handlers -----------------------------------------------------
  const handleSignatureTypeChange = useCallback(
    (next: SignatureType) => {
      if (disabled) return;
      setSignatureType(next);
      // Clear the signer on type change; a compatible signer must be re-selected.
      setSigner(null);
    },
    [disabled]
  );

  // "Require peer review" is one requirement with two shapes: a named Encamp
  // staff reviewer (a real reviewer row) or the open queue any staff can claim
  // (peerReviewRequired). Derive the switch from both so naming someone in the
  // reviewer dialog doesn't read as switching peer review back off.
  const hasStaffReviewer = reviewers.some((r) => r.isStaff);
  const peerReviewOn = peerReviewRequired || hasStaffReviewer;

  const handlePeerReviewToggle = useCallback(
    (next: boolean) => {
      if (disabled) return;
      if (next) {
        // Starts as the open queue; the reviewer dialog narrows it to a person.
        setPeerReviewRequired(true);
        if (!reviewRequired) setReviewRequired(true);
        return;
      }
      setPeerReviewRequired(false);
      setReviewers((prev) => prev.filter((r) => !r.isStaff));
    },
    [disabled, reviewRequired]
  );

  // Shared by the direct save and the signer-conflict confirm so both apply the
  // same state; the confirm path only adds clearing the signer.
  const applyReviewersSave = useCallback(
    (nextReviewers: Reviewer[], nextReviewMode: ReviewMode) => {
      setReviewers(nextReviewers);
      reviewModeForm.setValue("reviewMode", nextReviewMode, { shouldDirty: true });
      if (nextReviewers.length > 0 && !reviewRequired) setReviewRequired(true);
      setReviewerDialogOpen(false);
    },
    [reviewRequired, reviewModeForm]
  );

  const handleReviewersSave = useCallback(
    (nextReviewers: Reviewer[], nextReviewMode: ReviewMode) => {
      // One person can't be both a reviewer and the signer.
      if (signer && nextReviewers.some((r) => r.id === signer.id)) {
        setReviewersToConfirm({ reviewers: nextReviewers, reviewMode: nextReviewMode });
        return;
      }
      applyReviewersSave(nextReviewers, nextReviewMode);
    },
    [signer, applyReviewersSave]
  );

  const handleSignerSave = useCallback(
    (nextSigner: SubmissionReviewerSigner) => {
      if (reviewers.some((r) => r.id === nextSigner.id)) {
        setSignerToConfirm(nextSigner);
        return;
      }
      setSigner(nextSigner);
      setSignerDialogOpen(false);
    },
    [reviewers]
  );

  // Staff mark-complete/reset override: persist through updateSubmission; the
  // backend stamps completedAt when a reviewer becomes APPROVED.
  const persistReviewerOverride = useCallback(
    async (next: Reviewer[]) => {
      setReviewers(next);
      try {
        await updateSubmission({
          variables: {
            input: {
              id: submissionId,
              signatureType,
              reviewRequired,
              peerReviewRequired,
              reviewMode,
              reviewers: toReviewerInputs(buildOrderedConfig(next, signer)),
            },
          },
        });
        alerts.success("Reviewer status saved.");
      } catch (error) {
        alerts.error("Failed to save the reviewer status.", error);
      }
    },
    [
      updateSubmission,
      submissionId,
      signatureType,
      reviewRequired,
      peerReviewRequired,
      reviewMode,
      signer,
      alerts,
    ]
  );

  // Adds/reorders/deletes stay local until save/send; a reviewerStatus change is
  // the staff override and persists immediately.
  const handleReviewersDatagridChange = useCallback(
    (next: Reviewer[]) => {
      const statusChanged = next.some((r) => {
        const prev = reviewers.find((p) => p.id === r.id);
        return prev != null && prev.reviewerStatus !== r.reviewerStatus;
      });
      if (statusChanged) {
        const orderedReviewers = buildOrderedConfig(next, signer);
        const completesAllApprovals =
          orderedReviewers.length > 0 &&
          orderedReviewers.every((reviewer) => reviewer.reviewerStatus === ReviewerStatus.Approved);
        if (completesAllApprovals) {
          setFinalApprovalReviewers(next);
          return;
        }
        void persistReviewerOverride(next);
        return;
      }
      setReviewers(next);
    },
    [reviewers, signer, persistReviewerOverride]
  );

  // Plain save without advancing the flow; skips the validation gate since
  // half-finished config is exactly what an intermittent save is for.
  const handleSaveConfig = useCallback(async () => {
    try {
      await updateSubmission({
        variables: {
          input: {
            id: submissionId,
            signatureType,
            reviewRequired,
            peerReviewRequired: peerReviewToPersist,
            reviewMode,
            reviewers: toReviewerInputs(buildOrderedConfig(reviewersToPersist, signer)),
          },
        },
      });
      alerts.success("Changes saved.");
    } catch (error) {
      alerts.error("Failed to save changes.", error);
    }
  }, [
    updateSubmission,
    submissionId,
    signatureType,
    reviewRequired,
    peerReviewToPersist,
    reviewMode,
    reviewersToPersist,
    signer,
    alerts,
  ]);

  const handlePrepareForSigning = useCallback(async () => {
    const currentIssues = await revalidate();
    if (currentIssues.length > 0) {
      alerts.error("Resolve the validation issues before preparing the document for signing.");
      return;
    }
    try {
      // Persist the signature type + reviewer/signer config before opening the
      // BoldSign preparation dialog (which fetches its URL on mount).
      await updateSubmission({
        variables: {
          input: {
            id: submissionId,
            signatureType,
            reviewRequired,
            peerReviewRequired: peerReviewToPersist,
            reviewMode,
            reviewers: toReviewerInputs(buildOrderedConfig(reviewersToPersist, signer)),
          },
        },
      });
      setPreparationDialogOpen(true);
    } catch (error) {
      alerts.error("Failed to save before preparing for signing.", error);
    }
  }, [
    revalidate,
    updateSubmission,
    submissionId,
    signatureType,
    reviewRequired,
    peerReviewToPersist,
    reviewMode,
    reviewersToPersist,
    signer,
    alerts,
  ]);

  const handleSendForReview = useCallback(async () => {
    const currentIssues = await revalidate();
    if (currentIssues.length > 0) {
      alerts.error("Resolve the validation issues before sending for review.");
      return;
    }
    try {
      const result = await moveToReview({
        variables: {
          input: {
            id: submissionId,
            signatureType,
            reviewRequired,
            peerReviewRequired: peerReviewToPersist,
            reviewMode,
            reviewers: toReviewerInputs(buildOrderedConfig(reviewersToPersist, signer)),
          },
        },
      });
      if (result.data?.moveToReview) {
        // Sending starts the step; it completes only when the submission reaches
        // processing (driven by the final review action or the BoldSign webhook).
        await touchStep(SubmissionStepType.ReviewAndSign, SubmissionStepStatus.InProgress);
        alerts.success(
          result.data.moveToReview.status === SubmissionStatus.ProcessingSubmission
            ? "No review needed — submission moved to processing."
            : "Sent for review."
        );
      }
    } catch (error) {
      alerts.error("Failed to send for review.", error);
    }
  }, [
    revalidate,
    moveToReview,
    submissionId,
    signatureType,
    reviewRequired,
    peerReviewToPersist,
    reviewMode,
    reviewersToPersist,
    signer,
    touchStep,
    alerts,
  ]);

  const handleReviewSubmit = useCallback(
    async (values: ReviewFormValues) => {
      if (!currentUsersSubmissionDocumentReviewId) {
        alerts.error("Unable to locate a document review to update.");
        return;
      }
      try {
        if (values.reviewFinished) {
          await approveDocumentReview({
            variables: { submissionDocumentReviewId: currentUsersSubmissionDocumentReviewId },
          });
          alerts.success("Review completed. Thank you!");
        } else {
          await rejectDocumentReview({
            variables: {
              submissionDocumentReviewId: currentUsersSubmissionDocumentReviewId,
              rejectionComment: values.rejectionComment.trim(),
            },
          });
          alerts.success("Sent back to Encamp for edits.");
        }
      } catch (error) {
        const safeMessage = pickTransitionErrorMessage(error);
        if (safeMessage) {
          alerts.warning(`Your review was saved. ${safeMessage}`);
        } else {
          alerts.error("Unable to complete review. Please try again.", error);
        }
      }
    },
    [currentUsersSubmissionDocumentReviewId, approveDocumentReview, rejectDocumentReview, alerts]
  );

  const handleClaimPeerReview = useCallback(async () => {
    try {
      await assignPeerReviewToMe({ variables: { submissionId } });
      alerts.success("You're now assigned as the peer reviewer.");
    } catch (error) {
      alerts.error("Failed to assign peer review to you.", error);
    }
  }, [assignPeerReviewToMe, submissionId, alerts]);

  // --- Derived display flags ----------------------------------------------
  // Any staff reviewer withdraws the offer, or a second staff user could claim a
  // peer review someone else already owns and add a duplicate reviewer row.
  const canClaimPeerReview =
    isStaff &&
    submission?.peerReviewRequired === true &&
    status === SubmissionStatus.EncampInitialReview &&
    !persistedReviewers.some((r) => r.isStaff);

  const isWaitingForTurn = reviewState === SubmissionReviewState.REVIEWER_WAITING_FOR_TURN;
  const isSignerWaiting = reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS;
  const isProcessing = status === SubmissionStatus.ProcessingSubmission;
  const isAwaitingStaffWork =
    status === SubmissionStatus.NotStarted ||
    status === SubmissionStatus.InProgress ||
    status === SubmissionStatus.EncampInitialReview;

  // With nothing to review, sending skips review and lands in processing, so the
  // button is relabeled and gated by a confirm.
  const advancesWithoutReview = shouldAdvanceWithoutReview({
    reviewerCount: reviewersToPersist.length,
    hasSigner: !!signer,
    peerReviewRequired: peerReviewToPersist,
    reviewRequired,
  });

  // Nothing is under review until the submission has been sent. `reviewState`
  // alone doesn't say this — REVIEWER_CAN_REVIEW falls out of being on the
  // reviewer list with no turn taken, so it's already true while staff are still
  // building the config. Gate on the status instead.
  const isInReviewPhase =
    status === SubmissionStatus.EncampInitialReview || status === SubmissionStatus.InReview;

  const isDigitalSigner = isReviewerASigner && submission?.signatureType === SignatureType.Digital;
  const showReviewOrSignPanel =
    isInReviewPhase &&
    !(isDigitalSigner && signingCompleted) &&
    (reviewState === SubmissionReviewState.SIGNER_CAN_SIGN ||
      reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS ||
      (reviewState === SubmissionReviewState.REVIEWER_CAN_REVIEW && !isDigitalSigner));

  // Reviewing and configuring are different jobs, so the step does one at a
  // time. With a review to act on it leads with the review; staff can switch to
  // the configuration and back. Which side staff land on by default follows the
  // status. The header button overrides that, scoped to the phase it was pressed
  // in, so sending for review doesn't strand them on a stale choice.
  const [configOverride, setConfigOverride] = useState<{
    reviewPhase: boolean;
    showConfig: boolean;
  } | null>(null);
  const staffShowsConfig =
    configOverride?.reviewPhase === showReviewOrSignPanel
      ? configOverride.showConfig
      : !showReviewOrSignPanel;
  const showStaffConfig = isStaff && !disabled && staffShowsConfig;
  const inReviewMode = showReviewOrSignPanel && !showStaffConfig;

  const hasOtherPendingReviewers = persistedReviewers.some(
    (r) => !r.isSigner && r.user.id !== user?.id && !r.completedAt && !r.reviewerStatus
  );
  const hasCustomerReviewers = persistedReviewers.some((r) => !r.isSigner && !r.isStaff);

  // Read-only reviewer/signer/attachment displays for the review surface.
  // Customers don't see Encamp's internal reviewers.
  const { signer: persistedSigner, reviewers: persistedNonSignerReviewers } = useMemo(
    () => partitionReviewersAndSigners(persistedReviewers),
    [persistedReviewers]
  );
  const attachmentDocuments = useMemo(
    () => documents.filter((d) => d.type === SubmissionDocumentType.Attachment),
    [documents]
  );
  // Signed first, rejected last, so a superseded document is distinguishable.
  const submissionDocumentRows = useMemo(
    () =>
      sortSubmissionDocumentsByType(
        documents.filter((d) => d.type !== SubmissionDocumentType.Attachment).map(toDisplayDocument)
      ),
    [documents]
  );
  const attachmentRowsToDisplay = useMemo(
    () => attachmentDocuments.map(toDisplayDocument),
    [attachmentDocuments]
  );

  // A hasNoId alternate id is a recorded absence, not an id.
  const facilitiesToDisplay = useMemo(
    () =>
      (submission?.facilitySubmissions ?? []).flatMap((fs) => {
        const facility = fs.facility;
        if (!facility) return [];
        const epaAlt = facility.facilityAlternateIds?.find((a) => a.type === "EPA" && !a.hasNoId);
        return [
          {
            id: facility.id,
            display: `${facility.name}${
              facility.customerFacilityId ? ` (${facility.customerFacilityId})` : ""
            } - ${facility.streetAddress1}`,
            epaId: epaAlt?.value ?? null,
            href: tenantId ? buildWasteFacilityUrl(tenantId, facility.id) : null,
          },
        ];
      }),
    [submission?.facilitySubmissions, tenantId]
  );

  // Matches ReviewStep so the current reviewer's row reads "Your turn" rather
  // than "Pending" — but only once review has actually started, or an unsent
  // submission would claim it's their turn.
  const activeReviewerId =
    isInReviewPhase && reviewState === SubmissionReviewState.REVIEWER_CAN_REVIEW
      ? persistedReviewers.find((r) => !r.isSigner && r.user.id === user?.id)?.id
      : undefined;

  // The preview combines the review turn with the persisted preparation state
  // before exposing the signing action.
  const signingUrl = signingUrlData?.getEmbeddedDocumentSigningUrl ?? undefined;

  // When staff change the submission due date, the details dialog offers to
  // shift reviewer due dates by the same delta and persists them in one save.
  const detailsDueDateShift = useMemo(
    () => ({
      reviewers: reviewers.map((r) => ({
        id: r.id,
        name: r.name,
        email: r.email,
        dueDate: r.dueDate,
      })),
      onConfirm: (deltaDays: number) => {
        setReviewers((prev) =>
          prev.map((r) =>
            r.dueDate?.isValid ? { ...r, dueDate: r.dueDate.plus({ days: deltaDays }) } : r
          )
        );
      },
      buildReviewerInputs: (deltaDays: number) => {
        const shifted = reviewers.map((r) =>
          r.dueDate?.isValid ? { ...r, dueDate: r.dueDate.plus({ days: deltaDays }) } : r
        );
        return toReviewerInputs(buildOrderedConfig(shifted, signer));
      },
    }),
    [reviewers, signer]
  );

  const isSigningTurn =
    reviewState === SubmissionReviewState.SIGNER_CAN_SIGN ||
    reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS;

  let title = "Review & Sign";
  let description = isStaff
    ? "Configure reviewers and the signer, prepare the document for signing, then send it for review."
    : "Review and sign this submission when it's your turn.";
  if (inReviewMode) {
    title = isSigningTurn ? "Sign Submission" : "Review Submission";
    description = isSigningTurn
      ? "Read the document below, then add your signature to complete approval. If it needs changes, send it back for edits."
      : "Read the document below, then approve it. If it needs changes, send it back for edits.";
  }

  return (
    <SubmissionFormProvider issues={displayIssues} dueDateWarningsByUserId={dueDateWarnings}>
      <Stack spacing={3}>
        <StepHeader
          title={title}
          description={description}
          titleAdornment={
            isStaff ? (
              <SubmissionStatusChipWithPopover
                submissionId={submissionId}
                submissionStatus={status}
                encampStatus={submission?.encampStatus ?? null}
                isStaff={isStaff}
                asStatusChip
              />
            ) : undefined
          }
          action={
            isStaff && !disabled ? (
              <Button
                // Viewing is a low-emphasis switch (text); returning to the
                // staff-only editing surface uses the staff orange.
                variant={showStaffConfig ? "text" : "contained"}
                color={showStaffConfig ? "primary" : "secondary"}
                startIcon={showStaffConfig ? <Visibility /> : <Edit />}
                onClick={() =>
                  setConfigOverride({
                    reviewPhase: showReviewOrSignPanel,
                    showConfig: !showStaffConfig,
                  })
                }
              >
                {showStaffConfig ? "View Submission" : "Edit Submission"}
              </Button>
            ) : undefined
          }
        />

        <DetailsCard dueDateShift={detailsDueDateShift} hideEditAction={!showStaffConfig} />

        {showStaffConfig && unattributedIssues.length > 0 && (
          <Alert severity="error">
            {unattributedIssues.map((issue, index) => (
              <div key={index}>{issue.message}</div>
            ))}
          </Alert>
        )}

        <SubmissionRevisionBanner
          tenantId={tenantId}
          revisedFromSubmission={submission?.revisedFromSubmission}
          revisionSubmissions={submission?.revisionSubmissions}
        />

        {showStaffConfig && submission && (
          <SignaturePreparationBanner
            submission={{
              status: submission.status,
              signatureType: submission.signatureType,
              documents: documents.map((d) => ({
                type: d.type,
                externalDocumentId: d.externalDocumentId,
              })),
              reviewers: persistedReviewers.map((r) => ({
                isSigner: r.isSigner,
                signatureStatus: r.signatureStatus,
              })),
            }}
          />
        )}

        {!isStaff && !isWaitingForTurn && !isSignerWaiting && isAwaitingStaffWork && (
          <Alert severity="warning" icon={<HourglassTop />}>
            <Typography fontWeight="bold">Submission not ready for review</Typography>
            <Typography variant="body2">
              This submission is being worked on by Encamp Staff and the reviewer(s) and signer will
              be notified when it is ready for review.
            </Typography>
          </Alert>
        )}

        {isWaitingForTurn && (
          <Alert severity="warning" icon={<HourglassTop />}>
            <Typography fontWeight="bold">Submission is not ready for review</Typography>
            <Typography variant="body2">
              An earlier reviewer hasn't finished yet. You'll be notified when it's your turn.
            </Typography>
          </Alert>
        )}

        {isSignerWaiting && (
          <Alert severity="warning" icon={<HourglassTop />}>
            <Typography fontWeight="bold">Submission not ready for signature</Typography>
            <Typography variant="body2">
              The reviewers haven't finished yet. You'll be notified when the document is ready to
              sign.
            </Typography>
          </Alert>
        )}

        {isProcessing && (
          <Alert severity="info">This submission is being processed by Encamp staff.</Alert>
        )}

        {isStaff && isRejected && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => setRejectionRoutingOpen(true)}>
                Resolve
              </Button>
            }
          >
            This submission has been <strong>rejected</strong> by a reviewer. Use rejection routing
            to determine next steps.
          </Alert>
        )}

        {canClaimPeerReview && (
          <Alert
            severity="info"
            icon={<PersonAdd />}
            action={
              <Button
                color="inherit"
                size="small"
                disabled={claiming}
                onClick={handleClaimPeerReview}
              >
                {claiming ? "Assigning..." : "Assign to Me"}
              </Button>
            }
          >
            This submission is in <strong>Initial Review</strong> with no peer reviewer assigned.
          </Alert>
        )}

        {showStaffConfig && (
          // Reviewers and Signature are distinct decisions; keep them clearly apart.
          <Stack spacing={4}>
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight="medium">
                  Reviewer(s)
                </Typography>
                <Tooltip title="Add reviewer">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Add reviewer"
                      onClick={() => setReviewerDialogOpen(true)}
                      disabled={disabled || !reviewRequired}
                      sx={{ color: !disabled && reviewRequired ? "primary.main" : "inherit" }}
                    >
                      <PersonAdd fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <RadioGroup
                row
                value={reviewRequired ? "true" : "false"}
                onChange={(e) => {
                  if (disabled) return;
                  const nextReviewRequired = e.target.value === "true";
                  setReviewRequired(nextReviewRequired);
                  // Peer review is a kind of review, so no review can't leave the
                  // open-queue flag armed behind the hidden controls.
                  if (!nextReviewRequired) setPeerReviewRequired(false);
                }}
              >
                <FormControlLabel
                  value="false"
                  disabled={disabled}
                  control={<Radio size="small" disabled={disabled} />}
                  label={<Typography variant="body2">No review required</Typography>}
                />
                <FormControlLabel
                  value="true"
                  disabled={disabled}
                  control={<Radio size="small" disabled={disabled} />}
                  label={<Typography variant="body2">Include reviewers</Typography>}
                />
              </RadioGroup>
              {reviewRequired && (
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={peerReviewOn}
                        onChange={(event) => handlePeerReviewToggle(event.target.checked)}
                        size="small"
                        disabled={disabled}
                      />
                    }
                    label={<Typography variant="body2">Require peer review</Typography>}
                  />
                  {peerReviewOn && !hasStaffReviewer && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Open to any Encamp staff member. Add a specific peer reviewer to assign it.
                    </Typography>
                  )}
                  <ReviewersDatagrid
                    reviewers={reviewers}
                    setReviewers={disabled ? undefined : handleReviewersDatagridChange}
                    reviewMode={reviewMode}
                    setValue={disabled ? undefined : reviewModeForm.setValue}
                    handleAddReviewer={() => setReviewerDialogOpen(true)}
                    submissionDueDate={
                      submission?.dueDate
                        ? parseDateLikeToLocalDateTime(submission.dueDate)
                        : undefined
                    }
                    disabled={disabled}
                    isStaff={isStaff}
                    submissionId={submissionId}
                    submissionEncampStatus={submission?.encampStatus ?? null}
                    activeReviewerId={activeReviewerId}
                  />
                  {reviewRequiredIssues.length > 0 && (
                    <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                      {reviewRequiredIssues.map((i, index) => (
                        <div key={index}>{i.message}</div>
                      ))}
                    </Alert>
                  )}
                </Box>
              )}
            </Box>

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight="medium">
                  Signature
                </Typography>
                <Tooltip title="Add signer">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Add signer"
                      onClick={() => setSignerDialogOpen(true)}
                      disabled={disabled || signatureType === SignatureType.None}
                      sx={{
                        color:
                          !disabled && signatureType !== SignatureType.None
                            ? "primary.main"
                            : "inherit",
                      }}
                    >
                      <PersonAdd fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <RadioGroup
                row
                value={signatureType}
                onChange={(e) => {
                  const next = Object.values(SignatureType).find((t) => t === e.target.value);
                  if (next) handleSignatureTypeChange(next);
                }}
              >
                <FormControlLabel
                  value={SignatureType.None}
                  disabled={disabled}
                  control={<Radio size="small" disabled={disabled} />}
                  label={<Typography variant="body2">No signature</Typography>}
                />
                <FormControlLabel
                  value={SignatureType.Digital}
                  disabled={disabled}
                  control={<Radio size="small" disabled={disabled} />}
                  label={<Typography variant="body2">Digital signature</Typography>}
                />
                <FormControlLabel
                  value={SignatureType.WetInk}
                  disabled={disabled}
                  control={<Radio size="small" disabled={disabled} />}
                  label={<Typography variant="body2">Wet-ink signature</Typography>}
                />
              </RadioGroup>
              {signatureType !== SignatureType.None && (
                <SignersDatagrid
                  signers={signer ? [signer] : []}
                  signatureType={signatureType}
                  handleAddSigner={() => setSignerDialogOpen(true)}
                  handleRemoveSigner={() => {
                    if (disabled) return;
                    setSigner(null);
                  }}
                  submissionDueDate={
                    submission?.dueDate
                      ? parseDateLikeToLocalDateTime(submission.dueDate)
                      : undefined
                  }
                  disabled={disabled}
                />
              )}
              {signatureTypeIssues.length > 0 && (
                <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                  {signatureTypeIssues.map((i, index) => (
                    <div key={index}>{i.message}</div>
                  ))}
                </Alert>
              )}
            </Box>

            {!disabled && (
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<Save />}
                  loading={saving}
                  disabled={!hasUnsavedChanges || sending}
                  onClick={handleSaveConfig}
                >
                  Save
                </Button>
                {canPrepareForSigning && isDigitalSignature && needsDocumentPreparation && (
                  <SaveAndPrepareForSigningButton
                    signer={signer}
                    issues={displayIssues}
                    documents={documents.map((d) => ({
                      id: d.document.id,
                      name: d.document.title,
                      documentType: d.document.documentType,
                      submissionDocumentType: d.type,
                      url: d.document.storageLink ?? undefined,
                      fileExtension: d.document.fileExtension,
                    }))}
                    loading={saving}
                    formLoading={false}
                    isSubmitting={saving}
                    isValidating={false}
                    onClick={handlePrepareForSigning}
                  />
                )}
                {canSendForReview && !(isDigitalSignature && needsDocumentPreparation) && (
                  <Button
                    variant="contained"
                    loading={sending || saving}
                    disabled={
                      (reviewRequired && reviewers.length === 0 && !peerReviewRequired) ||
                      issues.length > 0
                    }
                    endIcon={<Send />}
                    onClick={
                      advancesWithoutReview
                        ? () => setAdvanceConfirmOpen(true)
                        : handleSendForReview
                    }
                  >
                    {getMoveButtonLabel({ advancesWithoutReview, isStaff })}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        )}

        {!showStaffConfig && (
          <Stack spacing={3}>
            <SubmissionReadOnlySummary
              tenantId={tenantId}
              isStaff={isStaff}
              facilities={facilitiesToDisplay}
              reviewers={persistedNonSignerReviewers}
              signer={persistedSigner}
              recipient={submission?.recipient}
              manifest={submission?.processedManifestExceptionReport}
              showTitle={false}
              showDueDate={false}
              showStaffAssignee={false}
              showNotes={false}
            />

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Submission Document
              </Typography>
              {submissionDocumentRows.length > 0 ? (
                <DocumentUploadSection
                  documents={submissionDocumentRows}
                  onDeleteDocument={() => {}}
                  canDeleteDocument={() => false}
                  onSelectDocument={(doc) => setSelectedDocumentId(doc.id)}
                  tenantId={tenantId || undefined}
                />
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No submission documents
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Additional Attachment(s)
              </Typography>
              {attachmentRowsToDisplay.length > 0 ? (
                <DocumentUploadSection
                  documents={attachmentRowsToDisplay}
                  onDeleteDocument={() => {}}
                  canDeleteDocument={() => false}
                  onSelectDocument={(doc) => setSelectedDocumentId(doc.id)}
                  tenantId={tenantId || undefined}
                />
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No additional attachments
                </Typography>
              )}
            </Box>
          </Stack>
        )}

        {isDigitalSigner && (
          <SignatureConfirmationAlert
            signingCompleted={signingCompleted}
            signingFlowConfirmed={signingFlowConfirmed}
            confirmationState={signatureConfirmationState}
            isAwaitingPostSignatureTransition={isAwaitingPostSignatureTransition}
            onRetry={retrySignatureConfirmation}
          />
        )}

        {!showStaffConfig && selectedDocument && !documentUrl && !documentPreviewFailed && (
          <Skeleton
            data-testid="document-preview-loading"
            variant="rectangular"
            animation="wave"
            height={600}
            width="100%"
            sx={{ borderRadius: 0.5 }}
          />
        )}

        {!showStaffConfig && selectedDocument && documentPreviewFailed && (
          <Box
            sx={{
              minHeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Unable to preview this document.
            </Typography>
          </Box>
        )}

        {!showStaffConfig && documentUrl && (
          <SubmissionDocumentPreview
            previewDocumentUrl={documentUrl}
            fileExtension={selectedDocument?.document.fileExtension ?? undefined}
            reviewState={reviewState}
            signingCompleted={signingCompleted}
            signingPending={signingConfirmationPending}
            signatureType={submission?.signatureType}
            submissionStatus={submission?.status}
            isDocumentPreparedForSigning={isSelectedDocumentPreparedForSigning}
            isSigner={isReviewerASigner}
            onSigningDialogOpen={() => setSigningDialogOpen(true)}
          />
        )}

        {inReviewMode && (
          <SubmissionDisplayReviewOrSignDocument
            isReviewerASigner={isReviewerASigner}
            isCurrentUserStaff={isStaff}
            hasSigner={!!signer}
            hasCustomerReviewers={hasCustomerReviewers}
            hasOtherPendingReviewers={hasOtherPendingReviewers}
            reviewState={reviewState}
            reviewFinished={reviewFinished}
            rejectionComment={rejectionComment}
            submissionDocumentReviewId={currentUsersSubmissionDocumentReviewId}
            submissionStatus={submission?.status}
            signatureType={submission?.signatureType}
            signingCompleted={signingCompleted}
            isMutating={approving || rejecting || formState.isSubmitting}
            control={control}
            errors={formState.errors}
            onSubmit={handleSubmit(handleReviewSubmit)}
          />
        )}
      </Stack>

      {reviewerDialogOpen && (
        <ReviewerManagementDialog
          open={reviewerDialogOpen}
          onClose={() => setReviewerDialogOpen(false)}
          initialReviewers={reviewers}
          initialReviewMode={reviewMode}
          showEncampStaffPeerReview
          initialPeerReviewRequired={peerReviewRequired}
          submissionFacilityIds={[facilityId]}
          onSave={(nextReviewers, nextReviewMode, nextPeerReviewRequired) => {
            setPeerReviewRequired(nextPeerReviewRequired ?? false);
            handleReviewersSave(nextReviewers, nextReviewMode);
          }}
        />
      )}

      {signerDialogOpen && (
        <SignerDialog
          open={signerDialogOpen}
          onClose={() => setSignerDialogOpen(false)}
          initialSigner={signer}
          signatureType={signatureType}
          submissionFacilityIds={[facilityId]}
          // Lifts each edit so validation sees what is typed, not only what was saved.
          onChange={setSigner}
          onSave={handleSignerSave}
        />
      )}

      {reviewersToConfirm && (
        <ConfirmDialog
          open={!!reviewersToConfirm}
          onClose={() => setReviewersToConfirm(null)}
          onConfirm={() => {
            setSigner(null);
            applyReviewersSave(reviewersToConfirm.reviewers, reviewersToConfirm.reviewMode);
            setReviewersToConfirm(null);
          }}
          header="Add this person as a reviewer and remove them as the signer?"
          info="This person is currently set as the signer for this submission."
          confirmText="Yes, add as reviewer"
          cancelText="No, keep as signer"
        />
      )}

      {signerToConfirm && (
        <ConfirmDialog
          open={!!signerToConfirm}
          onClose={() => setSignerToConfirm(null)}
          onConfirm={() => {
            setReviewers((prev) => prev.filter((r) => r.id !== signerToConfirm.id));
            setSigner(signerToConfirm);
            setSignerToConfirm(null);
            setSignerDialogOpen(false);
          }}
          header="Add this person as the signer and remove them as a reviewer?"
          info="This person is already assigned to review this document."
          confirmText="Yes, add as signer"
          cancelText="No, keep as reviewer"
        />
      )}

      {preparationDialogOpen && (
        <EmbeddedDocumentPreparationUrlDialog
          open={preparationDialogOpen}
          onClose={() => setPreparationDialogOpen(false)}
          submissionId={submissionId}
          additionalRefetchQueries={["GetSubmissionStepper"]}
        />
      )}

      {signingDialogOpen && signingUrl && (
        <EmbeddedDocumentSigning
          open={signingDialogOpen}
          onClose={() => setSigningDialogOpen(false)}
          signingUrl={signingUrl}
          onSigned={markSignatureSubmitted}
        />
      )}

      {rejectionRoutingOpen && (
        <RejectionRoutingDialog
          open={rejectionRoutingOpen}
          onClose={() => setRejectionRoutingOpen(false)}
          submissionId={submissionId}
          additionalRefetchQueries={["GetSubmissionStepper"]}
        />
      )}

      {advanceConfirmOpen && (
        <ConfirmDialog
          open={advanceConfirmOpen}
          onClose={() => setAdvanceConfirmOpen(false)}
          onConfirm={() => {
            setAdvanceConfirmOpen(false);
            void handleSendForReview();
          }}
          header="No reviewers assigned"
          info="This submission has no reviewers, so it will skip review and move straight to Processing. You can return it to In Progress afterward if needed."
          confirmText="Move to Processing"
          cancelText="Cancel"
        />
      )}

      {finalApprovalReviewers && (
        <ConfirmDialog
          open={finalApprovalReviewers !== null}
          loading={saving}
          onClose={() => setFinalApprovalReviewers(null)}
          onConfirm={async () => {
            await persistReviewerOverride(finalApprovalReviewers);
            setFinalApprovalReviewers(null);
          }}
          header="Complete review and submit?"
          info="This approval completes the review and moves the submission into processing. This can't be undone."
          confirmText="Approve & Submit"
          cancelText="Cancel"
        />
      )}

      <UnfinishedChangesPrompt
        when={hasUnsavedChanges && !sending && !saving && !preparationDialogOpen}
        header="Close without saving?"
        confirmText="Yes, close without saving"
        info="You have unsaved reviewer, signer, or signature changes. Leaving will discard them."
      />
    </SubmissionFormProvider>
  );
}
